import React, { useState, useEffect, useRef, useCallback, lazy, Suspense, useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Container, CssBaseline, TextField, Box, Button, IconButton,
  Typography, LinearProgress, Link, AppBar, Toolbar, Avatar,
  Chip, Divider, Card, CardContent, ButtonGroup, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar,
  Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  CircularProgress, Tab, Tabs, Checkbox, FormControlLabel, FormGroup, Collapse,
  Radio, RadioGroup, FormControl,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import HistoryIcon from '@mui/icons-material/History';
import BarChartIcon from '@mui/icons-material/BarChart';
import BuildIcon from '@mui/icons-material/Build';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SpeedIcon from '@mui/icons-material/Speed';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import GroupsIcon from '@mui/icons-material/Groups';
import CommentIcon from '@mui/icons-material/Comment';
import ArticleIcon from '@mui/icons-material/Article';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CampaignIcon from '@mui/icons-material/Campaign';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import Fab from '@mui/material/Fab';
import FeedbackIcon from '@mui/icons-material/Feedback';

const MonitoringPage = lazy(() => import('./pages/MonitoringPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const ActiveAudiencePage = lazy(() => import('./pages/ActiveAudiencePage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const SharePage = lazy(() => import('./pages/SharePage'));
// const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
// const ClientParserPage = lazy(() => import('./pages/ClientParserPage'));
// const VkTokenHelpPage = lazy(() => import('./pages/VkTokenHelpPage'));
const PostsPage = lazy(() => import('./pages/PostsPage'));
const CommentsPage = lazy(() => import('./pages/CommentsPage'));
const SimilarGroupsPage = lazy(() => import('./pages/SimilarGroupsPage'));
const GroupsPage = lazy(() => import('./pages/GroupsPage'));
const DataToolsPage = lazy(() => import('./pages/DataToolsPage'));
const PostEngagementPage = lazy(() => import('./pages/PostEngagementPage'));
const PollsPage = lazy(() => import('./pages/PollsPage'));
const FriendsPage = lazy(() => import('./pages/FriendsPage'));
const AdsPage = lazy(() => import('./pages/AdsPage'));
const ContestPage = lazy(() => import('./pages/ContestPage'));
const GroupStatsPage = lazy(() => import('./pages/GroupStatsPage'));
const VkToolsPage = lazy(() => import('./pages/VkToolsPage'));
const PeopleSearchPage = lazy(() => import('./pages/PeopleSearchPage'));
const UserCommentsPage = lazy(() => import('./pages/UserCommentsPage'));
const ContactsPage = lazy(() => import('./pages/ContactsPage'));
const MediaActivityPage = lazy(() => import('./pages/MediaActivityPage'));
const MarketPage = lazy(() => import('./pages/MarketPage'));
import LandingPage from './pages/LandingPage';
import axios from 'axios';
import { extractVkGroupId } from './utils/vkApi';

// Stable per-browser guest token for unauthenticated visitors, so the backend
// isolates each guest's quota/results instead of sharing one 'anon' account.
function getGuestToken() {
  let t = localStorage.getItem('vk_guest_token');
  if (!t) {
    t = 'guest_' + ((window.crypto && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36));
    localStorage.setItem('vk_guest_token', t);
  }
  return t;
}

// Attach session token (or isolated guest token) to every request
axios.interceptors.request.use(config => {
  const token = localStorage.getItem('vk_session_token') || getGuestToken();
  if (token) config.headers['X-Session-Token'] = token;
  return config;
});

// On 401 clear session token only if we HAD one (expired session).
// Do NOT reload when there is no token — that causes an infinite reload
// loop because unauthenticated endpoints naturally return 401.
axios.interceptors.response.use(
  r => r,
  err => {
    if (err?.response?.status === 401) {
      const wasLoggedIn = !!localStorage.getItem('vk_user_id');
      localStorage.removeItem('vk_user_id');
      localStorage.removeItem('vk_user_name');
      localStorage.removeItem('vk_user_photo');
      if (wasLoggedIn) window.location.reload();
    }
    return Promise.reject(err);
  }
);

import { getTheme } from './theme';
import FilterForm from './components/FilterForm';
import AudienceTable, { calculateAge, getStatusLabel } from './components/AudienceTable';
const AudienceDashboard = lazy(() => import('./components/AudienceDashboard'));
import SmartQueryBar from './components/SmartQueryBar';


// Browser desktop notifications
function requestNotifyPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}
function sendNotification(title, body) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    const n = new Notification(title, { body, icon: '/favicon.ico' });
    setTimeout(() => n.close(), 8000);
  } catch (_) {}
}

const DRAWER_WIDTH = 220;

// Common Container sizing for every routed page — extracted to keep the JSX
// terse and to ensure responsive padding stays in sync if it ever changes.
const PAGE_CONTAINER_SX = { py: { xs: 2, md: 4 }, px: { xs: 1.5, md: 3 } };

function downloadAsFile(content, filename, mime = 'text/plain;charset=utf-8') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const navSections = [
  {
    label: 'Парсинг',
    items: [
      { value: 'dashboard', label: 'Обзор', icon: <DashboardIcon fontSize="small" /> },
      { value: 'parser', label: 'Парсер', icon: <SearchIcon fontSize="small" /> },
      { value: 'active', label: 'Активная аудитория', icon: <PeopleIcon fontSize="small" /> },
      { value: 'monitoring', label: 'Мониторинг', icon: <MonitorHeartIcon fontSize="small" /> },
      // { value: 'client-parser', label: 'Клиентский парсер', icon: <SpeedIcon fontSize="small" /> },
      // { value: 'vk-token-help', label: 'Как получить токен', icon: <VpnKeyIcon fontSize="small" /> },
      { value: 'posts', label: 'Парсинг постов', icon: <ArticleIcon fontSize="small" /> },
      { value: 'comments', label: 'Парсинг комментариев', icon: <CommentIcon fontSize="small" /> },
      { value: 'groups-search', label: 'Фильтр групп', icon: <GroupsIcon fontSize="small" /> },
      { value: 'similar-groups', label: 'Похожие группы', icon: <GroupsIcon fontSize="small" /> },
      { value: 'post-engagement', label: 'Активность поста', icon: <ThumbUpIcon fontSize="small" /> },
      { value: 'polls', label: 'Опросы', icon: <BarChartIcon fontSize="small" /> },
      { value: 'friends', label: 'Друзья и подписчики', icon: <PeopleIcon fontSize="small" /> },
      { value: 'ads', label: 'Конкурентная реклама', icon: <CampaignIcon fontSize="small" /> },
      { value: 'contest', label: 'Победитель конкурса', icon: <EmojiEventsIcon fontSize="small" /> },
      { value: 'group-stats', label: 'Аналитика сообщества', icon: <BarChartIcon fontSize="small" /> },
      { value: 'vktools', label: 'VK-инструменты', icon: <BuildIcon fontSize="small" /> },
      { value: 'people-search', label: 'Поиск людей', icon: <SearchIcon fontSize="small" /> },
      { value: 'user-comments', label: 'Комментарии пользователя', icon: <CommentIcon fontSize="small" /> },
      { value: 'media-activity', label: 'Активность фото/видео', icon: <PhotoLibraryIcon fontSize="small" /> },
      { value: 'market', label: 'Магазин', icon: <ShoppingCartIcon fontSize="small" /> },
      { value: 'contacts', label: 'Email и телефоны', icon: <ContactMailIcon fontSize="small" /> },
    ],
  },
  {
    label: 'Данные',
    items: [
      { value: 'history', label: 'История', icon: <HistoryIcon fontSize="small" /> },
      { value: 'analytics', label: 'Аналитика', icon: <BarChartIcon fontSize="small" /> },
      { value: 'tools', label: 'Инструменты', icon: <BuildIcon fontSize="small" /> },
      { value: 'data-tools', label: 'Обработка данных', icon: <BlurOnIcon fontSize="small" /> },
    ],
  },
  {
    label: 'Управление',
    items: [
      { value: 'billing', label: 'Тариф и оплата', icon: <CreditCardIcon fontSize="small" /> },
      // { value: 'apikeys', label: 'Команда', icon: <GroupIcon fontSize="small" /> },
      { value: 'settings', label: 'Настройки', icon: <SettingsIcon fontSize="small" /> },
    ],
  },
];

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('UI crash:', error, info); }
  render() {
    if (this.state.hasError) {
      const msg = this.state.error ? String(this.state.error) : 'неизвестная ошибка';
      return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>Что-то пошло не так</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Обновите страницу. Если ошибка повторяется — очистите кэш браузера (Ctrl+Shift+R).
          </Typography>
          <Box component="pre" sx={{ mb: 3, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: 11, textAlign: 'left', maxWidth: '100%', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#c00' }}>
            {msg}
          </Box>
          <Button variant="contained" onClick={() => window.location.reload()}>Обновить страницу</Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [token, setToken] = useState('');
  const [groupIds, setGroupIds] = useState(['']);
  const [filters, setFilters] = useState({});
  const [audience, setAudience] = useState([]);
  const [largeResultNotice, setLargeResultNotice] = useState(null);
  const [audienceTotal, setAudienceTotal] = useState(0);
  const [parseSkipped, setParseSkipped] = useState(0);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [selectedFields, setSelectedFields] = useState({
    photo: true, first_name: true, last_name: true, sex: true,
    age: true, relation: true, city: true, country: true,
    education: true, occupation: true, online: true, followers: true, link: true,
  });
  const allFieldsSelected = Object.values(selectedFields).every(Boolean);
  const toggleField = (key) => setSelectedFields(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleAllFields = () => {
    const next = !allFieldsSelected;
    setSelectedFields(prev => Object.fromEntries(Object.keys(prev).map(k => [k, next])));
  };
  const [loading, setLoading] = useState(false);

  const [progressMessage, setProgressMessage] = useState('');
  const [progressCurrent, setProgressCurrent] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const [hasDefaultToken, setHasDefaultToken] = useState(false);
  const [hasPersonalToken, setHasPersonalToken] = useState(false);
  const [tokenPreference, setTokenPreference] = useState("server");
  const [poolTokenCount, setPoolTokenCount] = useState(0);
  const intervalRef = useRef(null);
  const startPollingRef = useRef(null);
  const pendingSinceRef = useRef(null);
  const vkidContainerRef = useRef(null);
  const [activePage, setActivePage] = useState(() => localStorage.getItem('vk_active_page') || 'parser');
  const [tokenHelpOpen, setTokenHelpOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackCopied, setFeedbackCopied] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [validationErrorSeverity, setValidationErrorSeverity] = useState('warning');
  const [idsOnly, setIdsOnly] = useState(false);
  const [currentHistoryId, setCurrentHistoryId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [vkAdsConfirmOpen, setVkAdsConfirmOpen] = useState(false);
  const [planLimit, setPlanLimit] = useState(10000);
  const [planName, setPlanName] = useState('Free');
  const [profilesUsed, setProfilesUsed] = useState(0);
  const [groupPreviews, setGroupPreviews] = useState({});
  const previewTimers = useRef({});
  const [vkUser, setVkUser] = useState(null); // { id, name, photo } — заполняется после Yandex OAuth
  const systemDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const [themeMode, setThemeMode] = useState(
    () => localStorage.getItem('theme_mode') || null
  );
  const resolvedMode = themeMode ?? (systemDark ? 'dark' : 'light');
  const theme = useMemo(() => getTheme(resolvedMode), [resolvedMode]);
  const toggleTheme = () => {
    const next = resolvedMode === 'dark' ? 'light' : 'dark';
    setThemeMode(next);
    localStorage.setItem('theme_mode', next);
  };
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('vk_active_page', activePage);
  }, [activePage]);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authTab, setAuthTab] = useState(0); // 0=login 1=register
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const shareMatch = window.location.pathname.match(/^\/s\/([A-Za-z0-9_-]+)/);

  const loadBilling = useCallback(() => {
    // Logged-in users hit /billing/status/<their_id>; guests hit /billing/status/anon.
    // Both work because the backend resolves the caller via X-Session-Token header
    // and falls back to a shared 'anon' user when none is provided.
    const uid = localStorage.getItem('vk_user_id') || 'anon';
    axios.get(`/billing/status/${uid}`).then(r => {
      setProfilesUsed(r.data.profiles_used ?? 0);
      setPlanLimit(r.data.profiles_limit ?? 10000);
      const pk = r.data.plan ?? 'free';
      const _planLabels = { free: 'Free', starter: 'Базовый', pro: 'Pro', agency: 'Agency' };
      setPlanName(_planLabels[pk] || pk.charAt(0).toUpperCase() + pk.slice(1));
    }).catch(() => {
      axios.get('/billing/plans').then(r => {
        const free = (r.data || []).find(p => p.key === 'free');
        if (free) { setPlanLimit(free.profiles_per_month ?? 10000); setPlanName(free.name || 'Free'); }
      }).catch(() => {});
    });
  }, []);

  const loadConfig = useCallback(() => {
    axios.get('/config').then(r => {
      setHasDefaultToken(r.data.has_default_token);
      setPoolTokenCount(r.data.pool_token_count || 0);
      setHasPersonalToken(r.data.has_personal_token || false);
      setTokenPreference(r.data.token_preference || 'server');
    });
  }, []);

  useEffect(() => {
    if (shareMatch) return;
    loadConfig();

    // Load VK user from localStorage (set after OAuth callback)
    const storedId = localStorage.getItem('vk_user_id');
    const storedName = localStorage.getItem('vk_user_name');
    const storedPhoto = localStorage.getItem('vk_user_photo');
    if (storedId) {
      setVkUser({ id: storedId, name: storedName || '', photo: storedPhoto || '' });
    }

    // Load billing on mount; subsequent updates are event-driven:
    // login (applyAuthSession), task completion (onTaskCompleted), billing page mount.
    loadBilling();
  }, [loadBilling, loadConfig]);

  // Auto-refresh billing counter every 30 s while logged in.
  useEffect(() => {
    if (!vkUser) return;
    const interval = setInterval(loadBilling, 30000);
    return () => clearInterval(interval);
  }, [vkUser, loadBilling]);

  // Cross-page navigation bus: child components dispatch
  // `window.dispatchEvent(new CustomEvent('vkp:navigate', { detail: 'settings' }))`
  // to switch the active page (used by error banners that link to settings).
  // Cheaper than wiring a context for one-off action buttons.
  useEffect(() => {
    const handler = (e) => {
      const target = e?.detail;
      if (typeof target === 'string' && target) {
        setActivePage(target);
      }
    };
    window.addEventListener('vkp:navigate', handler);
    return () => window.removeEventListener('vkp:navigate', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const task_id = e?.detail?.task_id;
      if (!task_id) return;
      setActivePage('parser');
      setLoading(true);
      setProgressMessage('Парсинг запущен...');
      setAudience([]);
      setLargeResultNotice(null);
      setAudienceTotal(0);
      setProgressCurrent(0);
      setProgressTotal(0);
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      startPollingRef.current(task_id);
    };
    window.addEventListener('vkp:track-task', handler);
    return () => window.removeEventListener('vkp:track-task', handler);
  }, []);

  if (shareMatch) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>}>
          <SharePage token={shareMatch[1]} />
        </Suspense>
      </ThemeProvider>
    );
  }

  const fetchGroupPreview = (index, value) => {
    const trimmed = value.trim();
    // Clear previous timer for this index
    if (previewTimers.current[index]) clearTimeout(previewTimers.current[index]);
    if (trimmed.length < 2) {
      setGroupPreviews(prev => { const n = { ...prev }; delete n[index]; return n; });
      return;
    }
    // Guard: VK screen names are <=32 chars and ids are short. Anything longer is
    // junk (e.g. an accidental paste) — don't fire the request (avoids 414 URI Too Long).
    if (trimmed.length > 64) {
      setGroupPreviews(prev => ({ ...prev, [index]: { error: true } }));
      return;
    }
    // Mark as loading
    setGroupPreviews(prev => ({ ...prev, [index]: { loading: true } }));
    previewTimers.current[index] = setTimeout(async () => {
      try {
        const res = await axios.get(`/tools/group-preview?group_id=${encodeURIComponent(trimmed)}`);
        setGroupPreviews(prev => ({ ...prev, [index]: res.data }));
      } catch (e) {
        // 429 = rate-limited (flood control), 400 = bad request — don't show not found
        if (e?.response?.status === 404) {
          setGroupPreviews(prev => ({ ...prev, [index]: { error: true } }));
        } else {
          // Transient error — just clear the loading state silently
          setGroupPreviews(prev => { const n = { ...prev }; delete n[index]; return n; });
        }
      }
    }, 700);
  };

  const handleGroupIdChange = (index, value) => {
    const cleaned = extractVkGroupId(value);
    const newGroupIds = [...groupIds];
    newGroupIds[index] = cleaned;
    setGroupIds(newGroupIds);
    fetchGroupPreview(index, cleaned);
  };

  const addGroupIdField = () => {
    setGroupIds([...groupIds, '']);
  };

  // Fix #3: allow removing any group as long as at least 1 remains
  const removeGroupIdField = (index) => {
    if (groupIds.length <= 1) return;
    const newGroupIds = groupIds.filter((_, i) => i !== index);
    setGroupIds(newGroupIds);
  };

  const handleSmartApply = (parsedFilters, sourceHint) => {
    setFilters(parsedFilters);
    if (sourceHint?.query) {
      // Pre-fill the first group field with the search hint so user can adjust
      setGroupIds(prev => {
        const updated = [...prev];
        if (updated[0] === '') updated[0] = sourceHint.query;
        else updated.unshift(sourceHint.query);
        return updated;
      });
    }
  };

  const stopParsePolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setLoading(false);
    setProgressMessage('');
  };

  const startPolling = (task_id) => {
    let pollErrors = 0;
    const doPoll = async () => {
      try {
        const res = await axios.get(`/tasks/${task_id}`);
        pollErrors = 0;
        const { status, result, error, progress, progress_current, progress_total } = res.data;
        if (status === 'pending') {
          if (!pendingSinceRef.current) pendingSinceRef.current = Date.now();
          const elapsed = Math.floor((Date.now() - pendingSinceRef.current) / 1000);
          if (elapsed < 8) {
            setProgressMessage('Создание задачи...');
          } else if (elapsed < 40) {
            setProgressMessage('Задача в очереди — ожидает воркера...');
          } else {
            setProgressMessage('Задача ожидает... Воркер, возможно, занят предыдущей задачей (rate-limit VK). Подождите или отмените.');
          }
          setProgressCurrent(0); setProgressTotal(0);
        } else if (status === 'running') {
          pendingSinceRef.current = null;
          setProgressMessage(progress || 'Парсинг выполняется...');
          if (progress_current > 0) setProgressCurrent(progress_current);
          if (progress_total  > 0) setProgressTotal(progress_total);
        } else if (status === 'completed') {
          stopParsePolling();
          const historyId = res.data.history_id;
          const resultCount = res.data.result_count || 0;
          if (historyId) setCurrentHistoryId(historyId);
          if (result && result.length > 0) {
            setAudience(result);
          } else if (historyId) {
            setProgressMessage('Загрузка ' + resultCount.toLocaleString() + ' профилей... Результаты уже сохранены в разделе История.');
            try {
              if (resultCount <= 10000) {
                const hr = await axios.get('/history/' + historyId + '?include_results=true');
                setAudience(hr.data.results || []);
              } else {
                const pr = await axios.get('/history/' + historyId + '/profiles?page=1&per_page=5000');
                setAudience(pr.data.profiles || []);
                setAudienceTotal(pr.data.total || resultCount);
                if ((pr.data.total || 0) > 5000) {
                  setLargeResultNotice({ historyId, resultCount: pr.data.total || resultCount });
                }
              }
            } catch (e) {
              console.warn('Could not load audience:', e);
              setAudience([]);
            }
          }
          setProgressMessage('');
          loadBilling();
          // Second call after a short delay in case usage commit races with task completion.
          setTimeout(loadBilling, 1500);
          sendNotification('Парсинг завершён', 'Найдено ' + resultCount.toLocaleString('ru-RU') + ' профилей');
          const skipped = res.data.skipped_count || 0;
          if (skipped > 0) setParseSkipped(skipped);
        } else if (status === 'failed') {
          stopParsePolling();
          sendNotification('Парсинг завершился с ошибкой', error || 'Проверьте раздел История');
          setValidationError("Ошибка: " + error);
        }
      } catch (err) {
        pollErrors += 1;
        console.warn('Poll error #' + pollErrors, err?.message);
        if (pollErrors >= 5) {
          stopParsePolling();
          setValidationError('Потеряна связь с сервером. Задача может всё ещё выполняться — проверьте результат в разделе «История».');
        } else {
          setProgressMessage('Ожидание ответа... (попытка ' + pollErrors + '/5)');
        }
      }
    };
    doPoll();
    intervalRef.current = setInterval(doPoll, 2000);
  };
  startPollingRef.current = startPolling;

  const handleCancel = stopParsePolling;

  const applyAuthSession = (data) => {
    localStorage.setItem('vk_session_token', data.session_token);
    localStorage.setItem('vk_user_id', data.user_id);
    localStorage.setItem('vk_user_name', data.name || '');
    localStorage.setItem('vk_user_photo', data.photo || '');
    setVkUser({ id: data.user_id, name: data.name || '', photo: data.photo || '' });
    setAuthDialogOpen(false);
    setAuthEmail(''); setAuthPassword(''); setAuthName(''); setAuthError('');
    loadBilling();
  };

  const [_vkBindSuccessCb, _setVkBindSuccessCb] = useState(null);
  const handleVkIdSuccess = async (data) => {
    try {
      const res = await axios.post('/auth/vk/token', {
        access_token: data.access_token,
        vk_user_id: data.user_id || null,
      });
      applyAuthSession(res.data);
      if (_vkBindSuccessCb) { _vkBindSuccessCb(); _setVkBindSuccessCb(null); }
      loadConfig();
    } catch (e) {
      console.error('VK ID auth failed', e);
    }
  };


  // VK ID callback handler: fires on mount if URL is /auth/vk/callback
  useEffect(() => {
    if (window.location.pathname !== '/auth/vk/callback') return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const deviceId = params.get('device_id');
    if (!code || !deviceId || !window.VKIDSDK) {
      window.location.replace('/');
      return;
    }
    const VKID = window.VKIDSDK;
    VKID.Config.init({
      app: 54607247,
      redirectUrl: 'https://vk-pars.ru/auth/vk/callback',
      responseMode: VKID.ConfigResponseMode.Callback,
      source: VKID.ConfigSource.LOWCODE,
      scope: 'groups',
    });
    VKID.Auth.exchangeCode(code, deviceId)
      .then((d) => handleVkIdSuccess(d).then(() => window.history.replaceState({}, '', '/')))
      .catch((err) => { console.error('VK callback exchange error', err); window.location.replace('/'); });
  }, []); // eslint-disable-line

  // OneTap widget in auth dialog
  useEffect(() => {
    if (!authDialogOpen) return;
    const timer = setTimeout(() => {
      if (!window.VKIDSDK || !vkidContainerRef.current) return;
      const VKID = window.VKIDSDK;
      VKID.Config.init({
        app: 54607247,
        redirectUrl: 'https://vk-pars.ru/auth/vk/callback',
        responseMode: VKID.ConfigResponseMode.Callback,
        source: VKID.ConfigSource.LOWCODE,
        scope: 'groups',
      });
      const oneTap = new VKID.OneTap();
      oneTap.render({
        container: vkidContainerRef.current,
        scheme: 'dark',
        showAlternativeLogin: true,
      })
      .on(VKID.WidgetEvents.ERROR, (err) => console.error('VKID OneTap error', err))
      .on(VKID.OneTapInternalEvents.LOGIN_SUCCESS, (payload) => {
        VKID.Auth.exchangeCode(payload.code, payload.device_id)
          .then(handleVkIdSuccess)
          .catch((err) => console.error('VKID exchange error', err));
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [authDialogOpen]); // eslint-disable-line

  // FloatingOneTap auto-popup DISABLED (UX): it auto-rendered on mount and
  // covered the header/CTA. Login now happens only when the user clicks a
  // "Войти"/CTA button, which opens the auth dialog and renders the in-dialog
  // OneTap widget (see the authDialogOpen effect above). No auto-render on mount.

  const handleAuthSubmit = async () => {
    setAuthError('');
    if (!authEmail.trim() || !authPassword) { setAuthError('Заполните все поля'); return; }
    setAuthLoading(true);
    try {
      const endpoint = authTab === 0 ? '/auth/login' : '/auth/register';
      const body = authTab === 0
        ? { email: authEmail.trim(), password: authPassword }
        : { email: authEmail.trim(), password: authPassword, name: authName.trim() || undefined };
      const res = await axios.post(endpoint, body);
      applyAuthSession(res.data);
    } catch (e) {
      setAuthError(e?.response?.data?.detail || 'Ошибка. Попробуйте ещё раз.');
    } finally {
      setAuthLoading(false);
    }
  };

  const _applyClientFilters = (profiles, activeFilters) => {
    return profiles.filter(u => {
      if (u.deactivated) return false;
      if (activeFilters.sex && u.sex !== Number(activeFilters.sex)) return false;
      if (activeFilters.has_photo === 1 && !u.has_photo) return false;
      if (activeFilters.has_photo === 0 && u.has_photo) return false;
      if (activeFilters.status && u.relation !== Number(activeFilters.status)) return false;
      if (activeFilters.city) {
        const cityTitle = (u.city?.title || '').toLowerCase();
        if (!cityTitle.includes(String(activeFilters.city).toLowerCase())) return false;
      }
      if (activeFilters.country) {
        const cTitle = (u.country?.title || '').toLowerCase();
        if (!cTitle.includes(String(activeFilters.country).toLowerCase())) return false;
      }
      if (activeFilters.followers_min && (u.followers_count || 0) < Number(activeFilters.followers_min)) return false;
      if (activeFilters.followers_max && (u.followers_count || 0) > Number(activeFilters.followers_max)) return false;
      if (activeFilters.age_from || activeFilters.age_to) {
        const parts = (u.bdate || '').split('.');
        if (parts.length < 3) return false;
        const [d, m, y] = parts.map(Number);
        const today = new Date();
        let age = today.getFullYear() - y;
        if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age--;
        if (activeFilters.age_from && age < Number(activeFilters.age_from)) return false;
        if (activeFilters.age_to && age > Number(activeFilters.age_to)) return false;
      }
      return true;
    });
  };

  const handleParse = async () => {
    const validGroupIds = groupIds.filter(id => id.trim() !== '');

    if (!token && !hasDefaultToken && poolTokenCount === 0) {
      setTokenHelpOpen(true);
      return;
    }
    if (validGroupIds.length === 0) {
      setValidationError('Введите хотя бы один ID сообщества.');
      return;
    }

    setLoading(true);
    requestNotifyPermission();
    setProgressMessage('Создание задачи...');
    setAudience([]);
    setLargeResultNotice(null);
    setAudienceTotal(0);
    setCurrentHistoryId(null);

    const body = {
      group_ids: validGroupIds,
      user_id: localStorage.getItem('vk_user_id') || 'demo',
    };
    if (token && !hasDefaultToken && poolTokenCount === 0) body.token = token;
    if (!idsOnly) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          body[key] = value;
        }
      });
    }
    if (idsOnly) body.ids_only = true;

    setProgressCurrent(0);
    setProgressTotal(0);
    setParseSkipped(0);
    let task_id;
    try {
      const res = await axios.post('/tasks', body);
      task_id = res.data.task_id;
    } catch (err) {
      console.error(err);
      const _status = err?.response?.status;
      const _detail = err?.response?.data?.detail || 'Произошла ошибка при создании задачи.';
      setValidationErrorSeverity(_status === 429 || _status === 402 ? 'error' : 'warning');
      setValidationError(_detail);
      setLoading(false);
      setProgressMessage('');
      return;
    }

    startPolling(task_id);
  };

  const _backendDownload = async (path, filename, prependText) => {
    setDownloading(true);
    try {
      const tok = localStorage.getItem('vk_session_token');
      const resp = await fetch(path, { headers: tok ? { 'X-Session-Token': tok } : {} });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const blob = await resp.blob();
      const finalBlob = prependText
        ? new Blob([prependText, blob], { type: blob.type })
        : blob;
      const url = URL.createObjectURL(finalBlob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) {
      setValidationError('Ошибка скачивания: ' + e.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleExport = async () => {
    if (currentHistoryId) {
      _backendDownload('/history/' + currentHistoryId + '/download/csv', 'vk_profiles.csv');
      return;
    }
    const XLSX = await import('xlsx');
    const formattedData = audience.map(user => ({
      'Имя': user.first_name,
      'Фамилия': user.last_name,
      'Пол': user.sex === 1 ? 'Женский' : (user.sex === 2 ? 'Мужской' : 'Не указан'),
      'Возраст': calculateAge(user.bdate),
      'Семейное положение': getStatusLabel(user.relation),
      'Город': user.city?.title || '',
      'Страна': user.country?.title || '',
      'Образование': user.university_name
        ? (user.faculty_name ? `${user.university_name}, ${user.faculty_name}` : user.university_name)
        : '',
      'Год окончания': user.graduation || '',
      'Работа/Учёба': user.occupation?.name || '',
      'О себе': user.about || '',
      'Статус': user.status || '',
      'Подписчики': user.followers_count || 0,
      'Интересы': user.interests || '',
      'Музыка': user.music || '',
      'Кино': user.movies || '',
      'Профиль': `https://vk.com/${user.domain || `id${user.id}`}`,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    const footerRow = {
      'Имя': '—',
      'Фамилия': '',
      'Пол': '',
      'Возраст': '',
      'Семейное положение': '',
      'Город': '',
      'Страна': '',
      'Образование': '',
      'Год окончания': '',
      'Работа/Учёба': '',
      'О себе': 'Собрано через VK Parser',
      'Статус': '',
      'Подписчики': '',
      'Интересы': '',
      'Музыка': '',
      'Кино': '',
      'Профиль': window.location.origin,
    };
    XLSX.utils.sheet_add_json(worksheet, [footerRow], { skipHeader: true, origin: -1 });
    XLSX.utils.book_append_sheet(workbook, worksheet, "Аудитория");
    XLSX.writeFile(workbook, "vk_audience.xlsx");
  };

  const downloadVkAds = () => {
    if (currentHistoryId) {
      _backendDownload('/history/' + currentHistoryId + '/download/xlsx-ids', 'vk_ads_audience.txt');
      return;
    }
    downloadAsFile(audience.map(u => u.id).join('\n'), 'vk_ads_audience.txt');
  };

  const handleExportVkAds = () => {
    if (!currentHistoryId && audience.length < 2000) {
      setVkAdsConfirmOpen(true);
      return;
    }
    downloadVkAds();
  };

  const handleExportMyTarget = () => {
    if (currentHistoryId) {
      _backendDownload('/history/' + currentHistoryId + '/download/xlsx-ids', 'mytarget_audience.csv', 'id\n');
      return;
    }
    downloadAsFile('id\n' + audience.map(u => u.id).join('\n'), 'mytarget_audience.csv');
  };

  // Sidebar nav — permanent on desktop, temporary drawer on mobile
  const sidebar = (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileNavOpen : true}
      onClose={() => setMobileNavOpen(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: { xs: 0, md: DRAWER_WIDTH },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          top: { xs: 0, md: '56px' },
          height: { xs: '100%', md: 'calc(100% - 56px)' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ overflow: 'auto', pt: 1, flexGrow: 1, minHeight: 0 }}>
        {navSections.map((section, si) => (
          <Box key={section.label}>
            {si > 0 && <Divider sx={{ my: 0.5 }} />}
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.disabled', fontSize: 10 }}
            >
              {section.label}
            </Typography>
            <List dense disablePadding>
              {section.items.map(item => (
                <ListItemButton
                  key={item.value}
                  selected={activePage === item.value}
                  onClick={() => { setActivePage(item.value); if (isMobile) setMobileNavOpen(false); }}
                  sx={{
                    borderRadius: 2,
                    mx: 1,
                    mb: 0.25,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(108,99,255,0.18)',
                      '& .MuiListItemIcon-root': { color: '#4B3FE0' },
                      '& .MuiListItemText-primary': { color: '#fff', fontWeight: 700 },
                    },
                    '&.Mui-selected:hover': { bgcolor: 'rgba(108,99,255,0.25)' },
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: activePage === item.value ? '#4B3FE0' : 'text.secondary' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 13,
                      fontWeight: activePage === item.value ? 700 : 400,
                      color: activePage === item.value ? '#fff' : 'text.secondary',
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        ))}
      </Box>
      {/* Tariff widget — flex child at the bottom of the Drawer so the menu
          scrolls above it and never overlaps. Single render handles desktop
          vertical card and mobile horizontal bar via responsive sx. */}
      <Card
        elevation={0}
        sx={{
          flexShrink: 0,
          m: { xs: 0, sm: 1.5 },
          p: { xs: '8px 16px', sm: 1.5 },
          borderRadius: { xs: 0, sm: 2 },
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          alignItems: { xs: 'center', sm: 'stretch' },
          gap: { xs: 1.5, sm: 0 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 0, sm: 0.5 }, flexShrink: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.4 }}>
            {planName.toUpperCase()} ТАРИФ
          </Typography>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%',
            bgcolor: (planLimit > 0 && profilesUsed >= planLimit) ? '#f44336' : '#4caf50',
            boxShadow: (planLimit > 0 && profilesUsed >= planLimit) ? '0 0 8px #f44336' : '0 0 8px #4caf50',
            ml: 1 }} />
        </Box>
        <Box sx={{ flex: { xs: 1, sm: 'unset' }, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5, whiteSpace: 'nowrap' }}>
            {(profilesUsed ?? 0).toLocaleString('ru-RU')}
            <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
              {(planLimit ?? 0) === -1 ? ' / ∞' : ` / ${(planLimit ?? 0).toLocaleString('ru-RU')}`} профилей
            </Typography>
          </Typography>
          <LinearProgress
            variant="determinate"
            value={planLimit > 0 ? Math.min(100, (profilesUsed / planLimit) * 100) : 0}
            sx={{ mb: { xs: 0, sm: 1 }, borderRadius: 2, height: 6, bgcolor: 'action.selected' }}
          />
        </Box>
        <Button
          size="small"
          variant="text"
          fullWidth
          sx={{ color: 'primary.main', fontSize: 12, textTransform: 'none', display: { xs: 'none', sm: 'block' } }}
          onClick={() => setActivePage('billing')}
        >
          Улучшить тариф →
        </Button>
      </Card>
    </Drawer>
  );


  // Landing page for unauthenticated users
  if (!vkUser) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LandingPage onLogin={() => { setAuthTab(0); setAuthError(''); setAuthDialogOpen(true); }} />
        <Dialog open={authDialogOpen} onClose={() => setAuthDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Вход</DialogTitle>
          <DialogContent>
            <Box ref={vkidContainerRef} sx={{ minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
          </DialogContent>
        </Dialog>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* Header */}
      <AppBar position="fixed" elevation={0} sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar variant="dense" sx={{ px: { xs: 1, md: 2 } }}>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileNavOpen(true)}
              sx={{ mr: 0.5 }}
              aria-label="menu"
            >
              <MenuIcon />
            </IconButton>
          )}
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: '#6C63FF',
              color: '#fff',
              fontWeight: 800,
              fontSize: 14,
              width: { xs: 30, md: 36 },
              height: { xs: 30, md: 36 },
              mr: { xs: 1, md: 1.5 },
            }}
          >
            VK
          </Avatar>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ flexGrow: 1, fontSize: { xs: 16, md: 20 } }}
          >
            VK Parser
          </Typography>
          <Tooltip title={resolvedMode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}>
            <IconButton onClick={toggleTheme} size="small" sx={{ mx: 0.5, color: 'text.primary' }}>
              {resolvedMode === 'dark'
                ? <Brightness7Icon fontSize="small" />
                : <Brightness4Icon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {vkUser ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
              {vkUser.photo ? (
                <Avatar src={vkUser.photo} sx={{ width: 28, height: 28 }} />
              ) : (
                <Avatar sx={{ width: 28, height: 28, bgcolor: '#6C63FF', fontSize: 13 }}>
                  {(vkUser.name || '?')[0]}
                </Avatar>
              )}
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  maxWidth: 120,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {vkUser.name}
              </Typography>
              <Button
                size="small"
                variant="text"
                sx={{ color: 'text.secondary', fontSize: 11, minWidth: 0, px: { xs: 0.5, md: 1 } }}
                onClick={() => {
                  localStorage.removeItem('vk_session_token');
                  localStorage.removeItem('vk_user_id');
                  localStorage.removeItem('vk_user_name');
                  localStorage.removeItem('vk_user_photo');
                  setVkUser(null);
                  loadBilling();
                }}
              >
                Выйти
              </Button>
            </Box>
          ) : (
            <Button
              size="small"
              variant="contained"
              sx={{
                bgcolor: '#6C63FF',
                '&:hover': { bgcolor: '#5a52d5' },
                color: '#fff',
                fontSize: { xs: 11, md: 12 },
                px: { xs: 1, md: 1.5 },
                whiteSpace: 'nowrap',
                textTransform: 'none',
              }}
              onClick={() => { setAuthTab(0); setAuthError(''); setAuthDialogOpen(true); }}
            >
              Войти
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Layout: sidebar + content */}
      <Box sx={{ display: 'flex', pt: '56px' }}>
        {sidebar}

        {/* Main content */}
        <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>}>

          {activePage === 'dashboard' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <DashboardPage />
            </Container>
          )}

          {activePage === 'parser' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>

              <SmartQueryBar onApply={handleSmartApply} disabled={loading} />

              {/* Card: Источники аудитории */}
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Источники аудитории
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {/* Fix #2: token field only shown when no token available */}
                  {!hasDefaultToken && poolTokenCount === 0 && (
                    <TextField
                      fullWidth
                      label="VK Access Token"
                      variant="outlined"
                      margin="normal"
                      value={token}
                      onChange={(e) => {
                        setToken(e.target.value);
                        if (e.target.value.trim().length > 20) {
                          localStorage.setItem('vk_manual_token', e.target.value.trim());
                          window.dispatchEvent(new CustomEvent('vkp:token-changed', { detail: e.target.value.trim() }));
                        }
                      }}
                      helperText={<span>Вставьте сюда ваш сервисный ключ доступа.{' '}<Link href="#" onClick={e => { e.preventDefault(); setActivePage('settings'); }} sx={{ cursor: 'pointer' }}>Как получить сервисный токен →</Link></span>}
                    />
                  )}
                  {(hasDefaultToken || poolTokenCount > 0) && (
                    <Chip
                      label={hasDefaultToken ? 'Серверный токен настроен' : `Пул токенов: ${poolTokenCount}`}
                      color="success"
                      size="small"
                      sx={{ mb: 2 }}
                    />
                  )}

                  {/* Fix #3: show delete button for any group when count > 1 */}
                  {groupIds.map((groupId, index) => {
                    const preview = groupPreviews[index];
                    return (
                      <Box key={index}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <TextField
                            fullWidth
                            label={`ID или короткое имя сообщества ${index + 1}`}
                            variant="outlined"
                            margin="normal"
                            value={groupId}
                            onChange={(e) => handleGroupIdChange(index, e.target.value)}
                            helperText={index === 0 && !preview ? "Например, 'durov' или 'club1'" : ""}
                            InputProps={{
                              endAdornment: preview?.loading ? (
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                              ) : null,
                            }}
                          />
                          {groupIds.length > 1 && (
                            <IconButton onClick={() => removeGroupIdField(index)} color="secondary" aria-label="Удалить группу">
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                        {preview && !preview.loading && !preview.error && (
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            mt: -1, mb: 1, ml: 0.5,
                          }}>
                            {preview.photo && (
                              <Box component="img" src={preview.photo} alt=""
                                sx={{ width: 24, height: 24, borderRadius: 1, objectFit: 'cover' }}
                              />
                            )}
                            <Typography variant="caption" sx={{ color: '#00D4AA', fontWeight: 600 }}>
                              {preview.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {preview.members_count?.toLocaleString('ru-RU')} подписчиков
                            </Typography>
                          </Box>
                        )}
                        {preview?.error && (
                          <Typography variant="caption" sx={{ color: '#FF6B6B', ml: 0.5, mt: -1, display: 'block', mb: 1 }}>
                            Группа не найдена
                          </Typography>
                        )}
                      </Box>
                    );
                  })}

                  <Button
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={addGroupIdField}
                    sx={{ mt: 1 }}
                  >
                    Добавить группу
                  </Button>
                </CardContent>
              </Card>

              {/* Card: Фильтры */}
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Фильтры
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <FilterForm filters={filters} setFilters={setFilters} disabled={loading || idsOnly} />
                </CardContent>
              </Card>

                {/* fields-selector-panel */}
                <Box sx={{ mt: 1, mb: 1 }}>
                  <Box
                    onClick={() => !idsOnly && setFieldsOpen(p => !p)}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      cursor: idsOnly ? 'default' : 'pointer', py: 0.5, px: 1,
                      opacity: idsOnly ? 0.4 : 1,
                      border: '1px solid rgba(255,255,255,0.10)',
                      borderRadius: 1,
                      '&:hover': { background: idsOnly ? 'transparent' : 'rgba(108,99,255,0.08)' },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Поля для сбора ({Object.values(selectedFields).filter(Boolean).length}/13)
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#4B3FE0' }}>
                      {fieldsOpen ? '▲ Скрыть' : '▼ Выбрать'}
                    </Typography>
                  </Box>
                  <Collapse in={fieldsOpen}>
                    <Box sx={{
                      border: '1px solid rgba(255,255,255,0.10)', borderTop: 0,
                      borderRadius: '0 0 4px 4px', p: 1.5,
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ cursor: 'pointer', color: '#4B3FE0', '&:hover': { textDecoration: 'underline' } }}
                          onClick={toggleAllFields}
                        >
                          {allFieldsSelected ? 'Снять все' : 'Выбрать все'}
                        </Typography>
                      </Box>
                      <FormGroup row sx={{ gap: 0 }}>
                        {[
                          ['photo',      'Фото'],
                          ['first_name', 'Имя'],
                          ['last_name',  'Фамилия'],
                          ['sex',        'Пол'],
                          ['age',        'Возраст'],
                          ['relation',   'Семейное положение'],
                          ['city',       'Город'],
                          ['country',    'Страна'],
                          ['education',  'Образование'],
                          ['occupation', 'Работа/Учёба'],
                          ['online',     'Онлайн'],
                          ['followers',  'Подписчики'],
                          ['link',       'Ссылка ВКонтакте'],
                        ].map(([key, label]) => (
                          <FormControlLabel
                            key={key}
                            control={
                              <Checkbox
                                checked={selectedFields[key]}
                                onChange={() => toggleField(key)}
                                size="small"
                                sx={{ py: 0.3, color: '#6C63FF', '&.Mui-checked': { color: '#6C63FF' } }}
                              />
                            }
                            label={<Typography variant="caption">{label}</Typography>}
                            sx={{ width: '50%', mr: 0, ml: 0 }}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  </Collapse>
                </Box>
              {/* IDs-only toggle */}
              <FormControlLabel
                sx={{ mb: 1, ml: 0 }}
                control={
                  <Checkbox
                    checked={idsOnly}
                    onChange={e => setIdsOnly(e.target.checked)}
                    size="small"
                    sx={{ color: '#6C63FF', '&.Mui-checked': { color: '#6C63FF' } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: idsOnly ? 'text.primary' : 'text.secondary' }}>
                    Только ID <Typography component="span" variant="caption" sx={{ color: 'text.disabled' }}>(быстро, без данных профиля)</Typography>
                  </Typography>
                }
              />
              {/* Large-group hint — accurate info (no false "300k per request" limit,
                  no non-functional time-based slices). VK paginates the full member
                  list; the parse collects everything within the plan's quota. */}
              {(() => {
                const _maxMembers = Math.max(0, ...groupIds.map((_, i) => groupPreviews[i]?.members_count || 0));
                if (_maxMembers <= 100000) return null;
                return (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Большая группа (~{_maxMembers.toLocaleString('ru-RU')} участников).
                    Сбор полного списка может занять несколько минут — будет получено
                    максимально доступное число участников в пределах лимита вашего тарифа.
                  </Alert>
                );
              })()}
              {/* Fix #4: Prominent full-width CTA button */}
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                onClick={handleParse}
                disabled={loading}
                sx={{ mb: 2, py: 1.5, fontSize: 16, fontWeight: 700, color: "#fff" }}
              >
                {loading ? 'Парсинг...' : 'Запустить парсинг'}
              </Button>

              {/* Inline progress */}

              {loading && (
                <Card sx={{ mb: 3, border: '1px solid rgba(108,99,255,0.3)', bgcolor: 'rgba(108,99,255,0.05)' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <CircularProgress size={16} sx={{ flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ flexGrow: 1, color: 'text.primary' }}>
                        {progressCurrent > 0
                          ? String(progressCurrent.toLocaleString('ru-RU')) + ' из ' + String(progressTotal.toLocaleString('ru-RU')) + ' участников'
                          : (progressMessage || 'Запуск...')}
                      </Typography>
                      <Button size="small" color="error" variant="outlined" onClick={handleCancel} sx={{ whiteSpace: 'nowrap', py: 0.25 }}>
                        Отменить
                      </Button>
                    </Box>
                    {progressCurrent > 0 && progressTotal > 0 ? (
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.round(progressCurrent / progressTotal * 100))}
                        sx={{ borderRadius: 2, height: 6 }}
                      />
                    ) : (
                      <LinearProgress sx={{ borderRadius: 2, height: 6 }} />
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Большие группы (100k+ участников) могут парситься 5–15 минут. Не закрывайте страницу.
                      </Typography>
                      {progressCurrent > 0 && progressTotal > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                          {Math.min(100, Math.round(progressCurrent / progressTotal * 100))}%
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}

{largeResultNotice && !loading && (
                <Card sx={{ mb: 3, border: 1, borderColor: 'success.main' }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} color="success.main">
                      Парсинг завершён
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      Найдено: <strong>{largeResultNotice.resultCount.toLocaleString('ru-RU')}</strong> профилей
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Результат слишком большой для отображения в браузере — переходите в «Историю» для скачивания.
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button variant="contained" size="small" startIcon={<HistoryIcon />}
                        onClick={() => { setActivePage('history'); setLargeResultNotice(null); }}>
                        Открыть Историю
                      </Button>
                      <Button variant="outlined" size="small" startIcon={<DownloadIcon />}
                        onClick={async () => {
          const tok = localStorage.getItem('vk_session_token');
          const resp = await fetch('/history/' + largeResultNotice.historyId + '/download/xlsx-ids', { headers: tok ? {'X-Session-Token': tok} : {} });
          const blob = await resp.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url;
          a.download = 'vk_ids.txt'; document.body.appendChild(a); a.click();
          document.body.removeChild(a); URL.revokeObjectURL(url);
        }}>
                        Скачать ID (TXT)
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Результаты */}
              {(audience.length > 0 || (currentHistoryId && audienceTotal > 0)) && !loading && (
                <Card sx={{ mb: 3 }}>
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 1.5, md: 1 },
                        mb: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          Найдено: {(audienceTotal > 0 ? audienceTotal : audience.length).toLocaleString('ru-RU')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Данные получены из VK API
                        </Typography>
                        {parseSkipped > 0 && (
                          <Typography variant="caption" sx={{ color: "text.disabled", display: "block", mt: 0.5 }}>
                            Пропущено {parseSkipped.toLocaleString()} удалённых/заблокированных
                          </Typography>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          width: { xs: '100%', md: 'auto' },
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                          onClick={handleExport}
                          disabled={downloading}
                          sx={{ flex: { xs: 1, md: '0 0 auto' }, minWidth: 0 }}
                        >
                          {currentHistoryId ? 'CSV (все поля)' : 'XLSX'}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                          onClick={handleExportVkAds}
                          disabled={downloading}
                          sx={{ flex: { xs: 1, md: '0 0 auto' }, minWidth: 0 }}
                        >
                          VK Реклама
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                          onClick={handleExportMyTarget}
                          disabled={downloading}
                          sx={{ flex: { xs: 1, md: '0 0 auto' }, minWidth: 0 }}
                        >
                          myTarget
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              <AudienceDashboard audience={audience} />
              {audienceTotal > 0 && audience.length < audienceTotal && (
                <Box sx={{ mt: 1, mb: 1, px: 1 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Показано {audience.length.toLocaleString('ru-RU')} из {audienceTotal.toLocaleString('ru-RU')} профилей. Полный список — в разделе «История».
                  </Typography>
                </Box>
              )}
              <AudienceTable audience={audience} displayTotal={audienceTotal || undefined} selectedFields={selectedFields} />
            </Container>
          )}

          {activePage === 'monitoring' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <MonitoringPage />
            </Container>
          )}
          {activePage === 'active' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <ActiveAudiencePage hasDefaultToken={hasDefaultToken} poolTokenCount={poolTokenCount} onTaskCompleted={loadBilling} />
            </Container>
          )}
          {activePage === 'history' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <HistoryPage />
            </Container>
          )}
          {activePage === 'tools' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <ToolsPage hasDefaultToken={hasDefaultToken} />
            </Container>
          )}
          {activePage === 'analytics' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <AnalyticsPage hasDefaultToken={hasDefaultToken} poolTokenCount={poolTokenCount} />
            </Container>
          )}
          {activePage === 'settings' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <SettingsPage
                hasDefaultToken={hasDefaultToken}
                poolTokenCount={poolTokenCount}
                sessionToken={localStorage.getItem('vk_session_token')}
                onVkBind={(cb) => { _setVkBindSuccessCb(() => cb); setAuthDialogOpen(true); }}
                onTokenChange={loadConfig}
              />
            </Container>
          )}
          {activePage === 'billing' && (
            <Container maxWidth="md" sx={PAGE_CONTAINER_SX}>
              <BillingPage onPlanChanged={loadBilling} />
            </Container>
          )}


          {activePage === 'posts' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <PostsPage />
            </Container>
          )}
          {activePage === 'comments' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <CommentsPage />
            </Container>
          )}
          {activePage === 'groups-search' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <GroupsPage />
            </Container>
          )}
          {activePage === 'similar-groups' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <SimilarGroupsPage hasDefaultToken={hasDefaultToken} poolTokenCount={poolTokenCount} />
            </Container>
          )}

          {activePage === 'data-tools' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <DataToolsPage />
            </Container>
          )}
          {activePage === 'post-engagement' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <PostEngagementPage />
            </Container>
          )}
          {activePage === 'polls' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <PollsPage />
            </Container>
          )}
          {activePage === 'friends' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <FriendsPage />
            </Container>
          )}
          {activePage === 'ads' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <AdsPage />
            </Container>
          )}
          {activePage === 'contest' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <ContestPage />
            </Container>
          )}
          {activePage === 'group-stats' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <GroupStatsPage />
            </Container>
          )}
          {activePage === 'vktools' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <VkToolsPage />
            </Container>
          )}
          {activePage === 'people-search' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <PeopleSearchPage />
            </Container>
          )}
          {activePage === 'user-comments' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <UserCommentsPage />
            </Container>
          )}
          {activePage === 'contacts' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <ContactsPage />
            </Container>
          )}
          {activePage === 'media-activity' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <MediaActivityPage />
            </Container>
          )}
          {activePage === 'market' && (
            <Container maxWidth="lg" sx={PAGE_CONTAINER_SX}>
              <MarketPage />
            </Container>
          )}

          </Suspense>
        </Box>
      
      {/* Feedback FAB — subtle, bottom-right corner */}
      <Fab
        size="small"
        onClick={() => setFeedbackOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          bgcolor: 'background.paper',
          color: 'text.secondary',
          boxShadow: 2,
          '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
          zIndex: 1400,
        }}
        title="Обратная связь"
      >
        <FeedbackIcon fontSize="small" />
      </Fab>

      <Dialog
        open={feedbackOpen}
        onClose={() => { if (!feedbackSending) { setFeedbackOpen(false); setFeedbackText(''); setFeedbackCopied(false); setFeedbackSent(false); } }}
        maxWidth="xs" fullWidth
      >
        <DialogTitle>Обратная связь</DialogTitle>
        <DialogContent dividers>
          {feedbackSent ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="success.main" gutterBottom>✓ Сообщение отправлено!</Typography>
              <Typography variant="body2" color="text.secondary">
                Администраторы свяжутся с вами через ВКонтакте.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Нашли баг, хотите новую функцию или есть вопрос? Напишите — это поможет сделать сервис лучше.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
                label="Ваше сообщение"
                placeholder="Опишите проблему или пожелание..."
                value={feedbackText}
                onChange={e => setFeedbackText(e.target.value)}
                disabled={feedbackSending}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {feedbackSent ? (
            <Button variant="contained" onClick={() => { setFeedbackOpen(false); setFeedbackText(''); setFeedbackSent(false); }}>
              Закрыть
            </Button>
          ) : (
            <>
              <Button onClick={() => { setFeedbackOpen(false); setFeedbackText(''); setFeedbackCopied(false); }} disabled={feedbackSending}>
                Отмена
              </Button>
              <Button
                variant="contained"
                disabled={!feedbackText.trim() || feedbackSending}
                onClick={async () => {
                  setFeedbackSending(true);
                  try {
                    await axios.post('/feedback', { text: feedbackText, page: activePage });
                    setFeedbackSent(true);
                    setFeedbackText('');
                  } catch {
                    alert('Ошибка отправки. Попробуйте ещё раз.');
                  } finally {
                    setFeedbackSending(false);
                  }
                }}
              >
                {feedbackSending ? 'Отправка…' : 'Отправить'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

</Box>

      {/* Auth dialog */}
      <Dialog open={authDialogOpen} onClose={() => setAuthDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>Войти в VK Parser</DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 3 }}>
          <Box ref={vkidContainerRef} sx={{ minHeight: 56 }} />
          <Divider sx={{ my: 2 }}>или</Divider>
          <Button
            variant="outlined" fullWidth
            sx={{ textTransform: 'none', borderColor: '#FC3F1D', color: '#FC3F1D', '&:hover': { borderColor: '#e03516', bgcolor: 'rgba(252,63,29,0.06)' } }}
            onClick={() => { window.location.href = '/auth/yandex/login'; }}
          >
            Войти через Яндекс
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={tokenHelpOpen} onClose={() => setTokenHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Нужен VK Access Token</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph>
            Некоторым инструментам нужен ваш личный ключ доступа VK API. Это опционально —
            добавляйте токен, только если инструмент прямо об этом просит.
          </Typography>
          <Typography variant="body2" component="div">
            <b>1. Получить токен:</b>{' '}
            <Link href="https://vkhost.github.io/" target="_blank" rel="noreferrer">
              vkhost.github.io
            </Link>
            {' '}→ выберите VK Admin или Kate Mobile → скопируйте токен из адресной строки.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Это личный токен с широкими правами: он открывает доступ к вашим личным сообщениям,
            публикации записей от вашего имени и управлению сообществами. Никому его не передавайте
            и отзывайте в настройках VK, когда он больше не нужен.
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <b>2. Добавить токен в пул</b> на вкладке «Настройки». Токены из вашего пула
            используются только для ваших задач и не видны другим пользователям.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTokenHelpOpen(false)}>Понятно</Button>
          <Button
            variant="contained"
            onClick={() => { setActivePage('settings'); setTokenHelpOpen(false); }}
          >
            Открыть настройки
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!validationError}
        autoHideDuration={validationErrorSeverity === 'error' ? null : 4000}
        onClose={() => setValidationError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={validationErrorSeverity} onClose={() => setValidationError('')}
          action={validationErrorSeverity === 'error' ? (
            <Button color="inherit" size="small" onClick={() => { setValidationError(''); setActivePage('billing'); }}>
              Тарифы
            </Button>
          ) : undefined}
        >
          {validationError}
        </Alert>
      </Snackbar>

      <Dialog open={vkAdsConfirmOpen} onClose={() => setVkAdsConfirmOpen(false)}>
        <DialogTitle>Аудитория меньше минимума VK Рекламы</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Минимальный размер аудитории для VK Рекламы — 2000 записей. Сейчас найдено {audience.length}. Скачать всё равно?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVkAdsConfirmOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={() => { setVkAdsConfirmOpen(false); downloadVkAds(); }}
          >
            Скачать
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

function AppWithBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithBoundary;
